import type { ReferencedEntry } from 'contentful';
import React, { AllHTMLAttributes, FC, useEffect, useRef, useState } from 'react';

import { CarouselType, Topic } from 'api/contentful/contentTypes.cloverdotcom';
import { FeatureItem } from './FeatureItem/FeatureItem';
import { HeroItem } from './HeroItem/HeroItem';
import { ProductItem } from './ProductItem/ProductItem';
import { DeviceItemImage } from './DeviceItemImage/DeviceItemImage';
import { SquareItem } from './SquareItem/SquareItem';
import { CaseStudyItem } from './CaseStudyItem/CaseStudyItem';
import { flattenContentfulData } from 'services/cms/flattenContentful';

interface ICarouselItemProps extends AllHTMLAttributes<HTMLElement> {
  entry: ReferencedEntry<Topic>;
  carouselType?: CarouselType;
  isVisible?: boolean;
  hostEl?: HTMLElement;
  slideProps?: unknown;
  noImage?: boolean;
}

export const CarouselItem: FC<ICarouselItemProps> = ({
  carouselType,
  hostEl,
  slideProps,
  noImage,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tabIndex, setTabIndex] = useState<-1 | undefined>(undefined);

  useEffect(() => {
    const rootEl = ref.current;
    const parentEl = rootEl?.parentElement;
    if (!hostEl || !rootEl || !parentEl) {
      setTabIndex(-1);
      return;
    }

    setTimeout(() => {
      const offset = 2;
      const item = rootEl.getBoundingClientRect();
      const host = hostEl.getBoundingClientRect();
      const inside =
        Math.ceil(item.left) >= Math.floor(host.left - offset) &&
        Math.floor(item.right) <= Math.ceil(host.right + offset);

      setTabIndex(inside ? undefined : -1);

      if (inside) {
        parentEl.setAttribute('role', 'group');
        parentEl.removeAttribute('aria-hidden');
      } else {
        parentEl.setAttribute('aria-hidden', 'true');
        parentEl.removeAttribute('role');
      }
    }, 500);
  }, [hostEl, slideProps]); // add slideProps to activate each time slides are changed

  const renderItem = () => {
    switch (carouselType) {
      case CarouselType.PRODUCT:
        return <ProductItem tabIndex={tabIndex} {...props} />;
      case CarouselType.FEATURE:
        return <FeatureItem tabIndex={tabIndex} {...props} />;
      case CarouselType.SQUARE:
      case CarouselType.ACCORDION:
        return <SquareItem tabIndex={tabIndex} carouselType={carouselType} noImage={noImage} {...props} />;
      case CarouselType.DEVICE:
        return <DeviceItemImage tabIndex={tabIndex} {...props} />;
      case CarouselType.ACCORDION_CASE_STUDY:
        return <CaseStudyItem tabIndex={tabIndex} entry={flattenContentfulData(props.entry)} renderImage />;
      case CarouselType.HERO:
      default:
        return <HeroItem tabIndex={tabIndex} {...props} />;
    }
  };

  return <div ref={ref}>{renderItem()}</div>;
};
